import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import menuData from '../../services/content/menuList.json'

const separator = require('../../assets/images/icons/separator.svg').default

/* ──────────────────────────────────────────────
   Subtitle map — SEO-friendly taglines per category
   ────────────────────────────────────────────── */
const SUBTITLES = {
  'Hookah': 'premium hookah lounge',
  'Hookah Flavors': 'our flavor collection',
  'Appetizers & Small Bites': 'start your meal',
  'Soups': 'warm & comforting',
  'Mezze & Cold Plates': 'Mediterranean classics',
  'Signature Platters (For Sharing)': 'feast together',
  'Salads': 'fresh & healthy',
  "Chef's Specialties": 'house favorites',
  'Sides': 'perfect pairings',
  'Rice Lovers': 'wok-tossed & seasoned',
  'Signature Grilled Entrées': 'from the grill',
  'Noodles': 'stir-fried delights',
  'Pasta': 'Italian al dente',
  'Artisan Pizzas': 'hand-crafted',
  'Desserts': 'sweet endings',
  'Hot Coffee': 'freshly brewed',
  'Iced Coffee': 'chilled & bold',
  'Specialty Drinks': 'barista crafted',
  'Tea': 'steeped to perfection',
  'Drinks - Soda': 'cold refreshments',
  'Drinks - House Specials': 'signature beverages',
  'Beer': 'Chilled Beer Selection',
  'Whisky & Scotch': 'Scotch & Whiskey',
  'Tequila': 'Tequila',
  'Vodka': 'Vodka',
  'Cognac & Brandy': 'Cognac & Brandy',
}

/* ──────────────────────────────────────────────
   Slug helper — generates a stable id from a
   category name (also used for anchor links)
   ────────────────────────────────────────────── */
function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

/* ──────────────────────────────────────────────
   Static data derived once from menuList.json —
   powers the category nav, filters & search
   ────────────────────────────────────────────── */
const ALL_CATEGORIES = menuData.menu
const HOOKAH_CATEGORY = ALL_CATEGORIES.find(c => c.category === 'Hookah')
const HOOKAH_FLAVORS_CATEGORY = ALL_CATEGORIES.find(c => c.category === 'Hookah Flavors')
const OTHER_CATEGORIES = ALL_CATEGORIES.filter(
  c => c.category !== 'Hookah' && c.category !== 'Hookah Flavors'
)

/* Hookah Flavors gets its own standalone section (id="hookah-flavors") and
   nav pill right next to Hookah, so it can be browsed, filtered, scrollspied
   and deep-linked exactly like any other category. */
const NAV_CATEGORIES = [
  ...(HOOKAH_CATEGORY ? [{ id: 'hookah', label: 'Hookah' }] : []),
  ...(HOOKAH_FLAVORS_CATEGORY ? [{ id: 'hookah-flavors', label: 'Hookah Flavors' }] : []),
  ...OTHER_CATEGORIES.map(c => ({ id: slugify(c.category), label: c.category })),
]

/* ──────────────────────────────────────────────
   Parent categories — high-level groupings that
   also define the order sections render in.

   `showTitle: false` for Hookah & Desserts because
   their single category heading already says the
   same thing (a parent banner would just repeat it).

   Exported so a future "what are you here for?"
   landing popup can offer these as entry points —
   each parent title carries an anchor id
   (`parent-<id>`) to scroll to, while the menu stays
   on the "All" filter so users can keep scrolling
   freely through everything else.
   ────────────────────────────────────────────── */
export const PARENT_CATEGORIES = [
  {
    id: 'hookah',
    label: 'Hookah',
    showTitle: false,
    categories: ['Hookah', 'Hookah Flavors'],
  },
  {
    id: 'cafe',
    label: 'Café',
    showTitle: true,
    categories: ['Hot Coffee', 'Iced Coffee', 'Specialty Drinks', 'Tea'],
  },
  {
    id: 'beverages',
    label: 'Beverages',
    showTitle: true,
    categories: ['Drinks - House Specials', 'Mocktails', 'Drinks - Soda'],
  },
  {
    id: 'kitchen',
    label: 'From the Kitchen',
    showTitle: true,
    categories: [
      'Appetizers & Small Bites',
      'Soups',
      'Mezze & Cold Plates',
      'Signature Platters (For Sharing)',
      'Salads',
      "Chef's Specialties",
      'Sides',
      'Rice Lovers',
      'Signature Grilled Entrées',
      'Noodles',
      'Pasta',
      'Artisan Pizzas',
    ],
  },
  {
    id: 'desserts',
    label: 'Desserts',
    showTitle: false,
    categories: ['Desserts'],
  },
  {
    id: 'bar',
    label: 'Bar Selection',
    showTitle: true,
    note: 'We ID — must be 21 or Older to order alcohol or hookah',
    categories: [
      'Beer',
      'Whisky & Scotch',
      'Vodka',
      'Tequila',
      'Cognac & Brandy',
      'Signature Drink',
    ],
  },
]

const CATEGORY_BY_NAME = new Map(ALL_CATEGORIES.map(c => [c.category, c]))

const SEARCH_INDEX = (() => {
  const idx = []
  if (HOOKAH_CATEGORY) {
    (HOOKAH_CATEGORY.items || []).forEach(item => {
      idx.push({
        name: item.name,
        description: item.description,
        price: item.price,
        categoryLabel: 'Hookah',
        sectionId: 'hookah',
        type: 'item',
      })
    })
  }
  if (HOOKAH_FLAVORS_CATEGORY) {
    (HOOKAH_FLAVORS_CATEGORY.brands || []).forEach(brand => {
      (brand.flavors || []).forEach(flavor => {
        idx.push({
          name: flavor,
          description: `${brand.brand} · Hookah Flavor`,
          price: null,
          categoryLabel: 'Hookah Flavors',
          sectionId: 'hookah-flavors',
          type: 'flavor',
        })
      })
    })
  }
  OTHER_CATEGORIES.forEach(cat => {
    const sectionId = slugify(cat.category)
    ;(cat.items || []).forEach(item => {
      idx.push({
        name: item.name,
        description: item.description,
        price: item.price,
        priceMedium: item.price_medium,
        priceLarge: item.price_large,
        categoryLabel: cat.category,
        sectionId,
        type: 'item',
      })
    })
  })
  return idx
})()

/* ──────────────────────────────────────────────
   Single dish-block (used by menu-two layout)
   ────────────────────────────────────────────── */
function DishBlock({ item, isPizza }) {
  /* price display */
  let priceLabel
  if (isPizza) {
    priceLabel = `M $${item.price_medium} / L $${item.price_large}`
  } else if (item.price === 0 || item.price === undefined || item.price === null) {
    priceLabel = 'Ask'
  } else {
    priceLabel = `$${Number(item.price).toFixed(2)}`
  }

  return (
    <div className="dish-block">
      <div className="inner-box">
        <div className="title clearfix">
          <div className="ttl clearfix">
            <h5>
              <Link to="#">
                {item.name}
                {item.dietary && (
                  <span className="s-info">{item.dietary.toUpperCase()}</span>
                )}
              </Link>
            </h5>
          </div>
          <div className="price">
            <span>{priceLabel}</span>
          </div>
        </div>
        {item.description && (
          <div className="text desc">
            <Link to="#">{item.description}</Link>
          </div>
        )}
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────
   Note block — renders a category's footnote with
   each \n-separated sentence on its own line (plain
   text collapses newlines in HTML, so we split it
   ourselves rather than relying on CSS white-space)
   ────────────────────────────────────────────── */
function NoteBlock({ note }) {
  if (!note) return null
  const lines = note.split('\n').map(line => line.trim()).filter(Boolean)

  return (
    <div className="subtitle" style={{ marginTop: '8px' }}>
      {lines.map((line, i) => (
        <span key={i} style={{ fontSize: '12px', display: 'block' }}>
          {line}
        </span>
      ))}
    </div>
  )
}

/* ──────────────────────────────────────────────
   Parent category banner — groups several menu
   sections under one high-level heading
   ────────────────────────────────────────────── */
function ParentTitle({ parent }) {
  return (
    <div className="menu-parent-title" id={`parent-${parent.id}`}>
      <div className="auto-container">
        <div className="menu-parent-title-row">
          <span className="menu-parent-line"></span>
          <h2>{parent.label}</h2>
          <span className="menu-parent-line"></span>
        </div>
        {parent.note && (
          <p className="menu-parent-note">{parent.note}</p>
        )}
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────
   Category section — two-column dish-block list
   ────────────────────────────────────────────── */
function CategorySection({ category, index }) {
  const subtitle = SUBTITLES[category.category] || 'our selection'
  const isPizza = category.category === 'Artisan Pizzas'
  const items = category.items || []

  /* split items into two columns */
  const mid = Math.ceil(items.length / 2)
  const leftCol = items.slice(0, mid)
  const rightCol = items.slice(mid)

  /* Generate an id slug from category name for anchor links */
  const sectionId = slugify(category.category)

  return (
    <section className="menu-two" id={sectionId} key={index}>
      {/* {index % 3 === 0 && (
        <div className="left-bg">
          <img src={bg20} alt="" title="" />
        </div>
      )} */}
      <div className="auto-container">
        <div className="title-box centered">
          <div className="subtitle">
            <span>{subtitle}</span>
          </div>
          <div className="pattern-image">
            <img src={separator} alt="separator" />
          </div>
          <h2>{category.category}</h2>
          <NoteBlock note={category.note} />
        </div>

        <div className="row clearfix">
          <div className="menu-col col-lg-6 col-md-12 col-sm-12">
            <div className="inner">
              {leftCol.map((item, i) => (
                <DishBlock key={i} item={item} isPizza={isPizza} />
              ))}
            </div>
          </div>
          {rightCol.length > 0 && (
            <div className="menu-col col-lg-6 col-md-12 col-sm-12">
              <div className="inner">
                {rightCol.map((item, i) => (
                  <DishBlock key={i} item={item} isPizza={isPizza} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/* ──────────────────────────────────────────────
   Hookah section — items only; flavors are their
   own standalone HookahFlavorsSection below
   ────────────────────────────────────────────── */
function HookahCombinedSection({ hookahCategory }) {
  const items = hookahCategory.items || []

  const mid = Math.ceil(items.length / 2)
  const leftCol = items.slice(0, mid)
  const rightCol = items.slice(mid)

  return (
    <section className="menu-two" id="hookah">
      <div className="auto-container">
        <div className="title-box centered">
          <div className="subtitle">
            <span>{SUBTITLES['Hookah']}</span>
          </div>
          <div className="pattern-image">
            <img src={separator} alt="separator" />
          </div>
          <h2>{hookahCategory.category}</h2>
          <NoteBlock note={hookahCategory.note} />
        </div>

        <div className="row clearfix">
          <div className="menu-col col-lg-6 col-md-12 col-sm-12">
            <div className="inner">
              {leftCol.map((item, i) => (
                <DishBlock key={i} item={item} isPizza={false} />
              ))}
            </div>
          </div>
          {rightCol.length > 0 && (
            <div className="menu-col col-lg-6 col-md-12 col-sm-12">
              <div className="inner">
                {rightCol.map((item, i) => (
                  <DishBlock key={i} item={item} isPizza={false} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/* ──────────────────────────────────────────────
   Hookah Flavors — standalone section with its
   own anchor/nav pill right next to Hookah
   ────────────────────────────────────────────── */
function HookahFlavorsSection({ flavorsCategory }) {
  const brands = flavorsCategory.brands || []
  if (brands.length === 0) return null

  return (
    <section className="menu-two" id="hookah-flavors">
      <div className="auto-container">
        <div className="title-box centered">
          <div className="subtitle">
            <span>{SUBTITLES['Hookah Flavors']}</span>
          </div>
          <div className="pattern-image">
            <img src={separator} alt="separator" />
          </div>
          <h2>Hookah Flavors</h2>
        </div>

        <div className="row clearfix">
          {brands.map((brand, i) => (
            <div
              key={i}
              className="col-lg-4 col-md-6 col-sm-12"
              style={{ marginBottom: '30px', textAlign: 'left' }}
            >
              <h4
                style={{
                  fontFamily: 'var(--font-two)',
                  color: 'var(--main-color)',
                  marginBottom: '12px',
                  fontSize: '20px',
                }}
              >
                {brand.brand}
              </h4>
              <div
                style={{
                  color: 'var(--main-color)',
                  opacity: 0.7,
                  fontSize: '14px',
                  lineHeight: '2',
                  fontFamily: 'var(--font-three)',
                }}
              >
                {brand.flavors.join('  •  ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ──────────────────────────────────────────────
   Menu navigation — sticky search + category
   filter bar (search, filter pills, swipe nav)
   ────────────────────────────────────────────── */
function MenuNav({ activeId, onSelectCategory, onSelectResult }) {
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const pillRefs = useRef({})
  const pillsRowRef = useRef(null)
  const navRef = useRef(null)

  /* Center the active pill within the horizontal pill row only —
     never touches window/page scroll (avoids fighting the sticky nav). */
  useEffect(() => {
    const container = pillsRowRef.current
    const el = pillRefs.current[activeId]
    if (container && el) {
      const targetLeft = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2
      container.scrollTo({ left: Math.max(targetLeft, 0), behavior: 'smooth' })
    }
  }, [activeId])

  /* The site header isn't a fixed height — it can be full-size, collapsed,
     or removed from the DOM entirely while scrolling (see Header/Main.js).
     Track its real bottom edge every frame so this nav always sits flush
     underneath it instead of assuming a fixed offset. */
  useEffect(() => {
    const navEl = navRef.current
    if (!navEl) return
    let rafId
    let lastTop = null

    const sync = () => {
      const header = document.querySelector('.main-header')
      const headerBottom = header ? Math.max(Math.round(header.getBoundingClientRect().bottom), 0) : 0
      if (headerBottom !== lastTop) {
        navEl.style.top = `${headerBottom}px`
        lastTop = headerBottom
      }
      rafId = requestAnimationFrame(sync)
    }
    sync()

    return () => cancelAnimationFrame(rafId)
  }, [])

  /* Convert vertical mouse-wheel/trackpad input into horizontal scrolling
     while hovering the pill row, so users aren't fighting the page scroll
     when they try to reveal categories further along the row. Only takes
     over when the row actually overflows horizontally.

     The dominant axis is decided ONCE per gesture (on its first wheel event)
     rather than per-event — trackpad swipes fire a whole stream of wheel
     events with naturally jittery deltaX/deltaY, so re-checking every event
     could flip between native horizontal scroll and our JS override mid-swipe
     and make horizontal swiping feel broken/stuttery. The gesture resets after
     a short idle gap so the next swipe is judged fresh. */
  useEffect(() => {
    const container = pillsRowRef.current
    if (!container) return

    let gestureIsVertical = null
    let idleTimer = null

    const handleWheel = e => {
      if (container.scrollWidth <= container.clientWidth) return

      clearTimeout(idleTimer)
      idleTimer = setTimeout(() => { gestureIsVertical = null }, 150)

      if (gestureIsVertical === null) {
        gestureIsVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX)
      }
      if (!gestureIsVertical) return

      container.scrollLeft += e.deltaY
      e.preventDefault()
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      container.removeEventListener('wheel', handleWheel)
      clearTimeout(idleTimer)
    }
  }, [])

  /* Track whether the pill row has more content off-screen to the left/right
     so the arrow buttons can disable themselves at either end instead of
     staying clickable with nothing left to reveal. */
  useEffect(() => {
    const container = pillsRowRef.current
    if (!container) return

    const updateScrollState = () => {
      setCanScrollLeft(container.scrollLeft > 2)
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 2)
    }

    updateScrollState()
    container.addEventListener('scroll', updateScrollState, { passive: true })

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [])

  const scrollPills = direction => {
    const container = pillsRowRef.current
    if (!container) return
    container.scrollBy({ left: direction * container.clientWidth * 0.7, behavior: 'smooth' })
  }

  const trimmed = query.trim().toLowerCase()
  const results = trimmed
    ? SEARCH_INDEX.filter(
        item =>
          item.name.toLowerCase().includes(trimmed) ||
          (item.description || '').toLowerCase().includes(trimmed)
      ).slice(0, 8)
    : []

  const handleResultClick = result => {
    setQuery('')
    setFocused(false)
    onSelectResult(result)
  }

  const priceLabel = result => {
    if (result.priceMedium !== undefined && result.priceMedium !== null) {
      return `M $${result.priceMedium} / L $${result.priceLarge}`
    }
    if (result.price === null || result.price === undefined) return ''
    return `$${Number(result.price).toFixed(2)}`
  }

  return (
    <div className="menu-nav-sticky" ref={navRef}>
      <div className="auto-container">
        <div className="menu-nav-inner">
          <div className="menu-search-wrap">
            <i className="fas fa-search menu-search-icon"></i>
            <input
              type="text"
              className="menu-search-input"
              placeholder="Search dishes, drinks, hookah flavors…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
            />
            {query && (
              <button
                type="button"
                className="menu-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}

            {focused && trimmed && (
              <div className="menu-search-results">
                {results.length === 0 ? (
                  <div className="menu-search-empty">No matches found</div>
                ) : (
                  results.map((r, i) => (
                    <button
                      type="button"
                      key={i}
                      className="menu-search-result"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleResultClick(r)}
                    >
                      <span className="msr-name">{r.name}</span>
                      <span className="msr-meta">
                        <span className="msr-cat">{r.categoryLabel}</span>
                        {priceLabel(r) && <span className="msr-price">{priceLabel(r)}</span>}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="menu-nav-pills-wrap">
            <button
              type="button"
              className="menu-nav-arrow menu-nav-arrow-left"
              onClick={() => scrollPills(-1)}
              disabled={!canScrollLeft}
              aria-label="Scroll categories left"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            <div className="menu-nav-pills" ref={pillsRowRef}>
              <button
                type="button"
                ref={el => { pillRefs.current.all = el }}
                className={`menu-nav-pill${activeId === 'all' ? ' active' : ''}`}
                onClick={() => onSelectCategory('all')}
              >
                <i className="fas fa-th"></i> All
              </button>
              {NAV_CATEGORIES.map(cat => (
                <button
                  type="button"
                  key={cat.id}
                  ref={el => { pillRefs.current[cat.id] = el }}
                  className={`menu-nav-pill${activeId === cat.id ? ' active' : ''}`}
                  onClick={() => onSelectCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="menu-nav-arrow menu-nav-arrow-right"
              onClick={() => scrollPills(1)}
              disabled={!canScrollRight}
              aria-label="Scroll categories right"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────
   Main FullMenu component — renders every
   category from menuList.json
   ────────────────────────────────────────────── */
function FullMenu() {
  const { hash } = useLocation()

  const [filterId, setFilterId] = useState('all')
  const [scrollSpyId, setScrollSpyId] = useState(NAV_CATEGORIES[0]?.id)
  const touchStartRef = useRef({ x: 0, y: 0 })

  const activeId = filterId === 'all' ? scrollSpyId : filterId

  const scrollToId = id => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const goToCategory = id => {
    setFilterId(id)
    scrollToId(id === 'all' ? 'menu-top' : id)
  }

  const handleSelectResult = result => {
    setFilterId(result.sectionId)
    scrollToId(result.sectionId)
  }

  /* Deep-link hash support (e.g. /menu#hookah) */
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash)
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300)
      }
    }
  }, [hash])

  /* Scrollspy — highlights the active pill while browsing the full menu.
     Tracks intersection state for every section (not just the elements that
     changed in the latest callback) so the topmost visible one is always
     picked correctly. */
  useEffect(() => {
    if (filterId !== 'all') return
    const elements = NAV_CATEGORIES.map(cat => document.getElementById(cat.id)).filter(Boolean)
    if (elements.length === 0) return

    const sectionState = new Map()

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          sectionState.set(entry.target.id, {
            top: entry.boundingClientRect.top,
            isIntersecting: entry.isIntersecting,
          })
        })
        let best = null
        sectionState.forEach((info, id) => {
          if (info.isIntersecting && (!best || info.top < best.top)) {
            best = { id, top: info.top }
          }
        })
        if (best) setScrollSpyId(best.id)
      },
      { rootMargin: '-140px 0px -60% 0px', threshold: 0 }
    )
    elements.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [filterId])

  /* Mobile swipe — swipe left/right to move between filtered categories */
  const handleTouchStart = e => {
    const t = e.touches[0]
    touchStartRef.current = { x: t.clientX, y: t.clientY }
  }

  const handleTouchEnd = e => {
    if (filterId === 'all') return
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStartRef.current.x
    const dy = t.clientY - touchStartRef.current.y
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return

    const currentIndex = NAV_CATEGORIES.findIndex(c => c.id === filterId)
    if (currentIndex === -1) return
    if (dx < 0 && currentIndex < NAV_CATEGORIES.length - 1) {
      goToCategory(NAV_CATEGORIES[currentIndex + 1].id)
    } else if (dx > 0 && currentIndex > 0) {
      goToCategory(NAV_CATEGORIES[currentIndex - 1].id)
    }
  }

  return (
    <div className="full-menu-wrapper">

      {/* ── Global gratuity notice ── */}
      {menuData.globalNote && (
        <div className="auto-container" style={{ textAlign: 'center', paddingTop: '20px' }}>
          <div className="title-box centered">
            <div className="subtitle">
              <span>{menuData.globalNote}</span>
            </div>
            <div className="pattern-image">
              <img src={separator} alt="" />
            </div>
          </div>
        </div>
      )}

      <div id="menu-top"></div>

      <MenuNav
        activeId={activeId}
        onSelectCategory={goToCategory}
        onSelectResult={handleSelectResult}
      />

      {/* ── Prev / Next swipe toolbar — shown when a single category is active ── */}
      {/* {filterId !== 'all' && filteredIndex !== -1 && (
        <div className="auto-container">
          <div className="menu-swipe-toolbar">
            <button
              type="button"
              className="menu-swipe-btn"
              disabled={filteredIndex === 0}
              onClick={() => goToCategory(NAV_CATEGORIES[filteredIndex - 1].id)}
              aria-label="Previous category"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="menu-swipe-counter">
              {filteredIndex + 1} / {NAV_CATEGORIES.length} · swipe to browse
            </span>
            <button
              type="button"
              className="menu-swipe-btn"
              disabled={filteredIndex === NAV_CATEGORIES.length - 1}
              onClick={() => goToCategory(NAV_CATEGORIES[filteredIndex + 1].id)}
              aria-label="Next category"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )} */}

      <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {PARENT_CATEGORIES.map(parent => {
          /* Only the categories this filter actually shows */
          const visible = parent.categories.filter(
            name => filterId === 'all' || slugify(name) === filterId
          )
          if (visible.length === 0) return null

          return (
            <React.Fragment key={parent.id}>
              {parent.showTitle && <ParentTitle parent={parent} />}
              {visible.map((name, idx) => {
                if (name === 'Hookah') {
                  return HOOKAH_CATEGORY ? (
                    <HookahCombinedSection key={name} hookahCategory={HOOKAH_CATEGORY} />
                  ) : null
                }
                if (name === 'Hookah Flavors') {
                  return HOOKAH_FLAVORS_CATEGORY ? (
                    <HookahFlavorsSection key={name} flavorsCategory={HOOKAH_FLAVORS_CATEGORY} />
                  ) : null
                }
                const category = CATEGORY_BY_NAME.get(name)
                if (!category) return null
                return <CategorySection key={name} category={category} index={idx} />
              })}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}


export default FullMenu
